import { defaultSharingSettings } from '../reducers/content/sharing';
import { defaultContentSettings } from '../reducers/content/settings';
import { defaultSharingStudents } from '../reducers/content/sharing_students';
import { isInstructorInSection } from './current_user.js';
import R from 'ramda';
import { createSelector } from 'reselect';
import deprecated from '../deprecated';
import { selectLastPageRead } from './membership.js';
import { pageDecompressor } from '../page_compressor';
import { wordDecompressor } from '../word_compressor';

import { param } from './utils';

// selectLastPageRead makes more sense as a content selector, but it's attached
// to the membership. It's easier to just export an alias here than to move
// things.
export { selectLastPageRead };

const greaterThan = current => {
    const pagenum = current.pagenum;
    return next => next.pagenum > pagenum;
};

const lessThan = current => {
    const pagenum = current.pagenum;
    return next => next.pagenum < pagenum;
};

const chapter = data => ({
    name: null,
    first_page_label: null,
    last_page_label: null,
    first_page: null,
    last_page: null,
    notes: [],
    ...data,
});

// This is a NOOP, but it's here to be more explicit about the fact that we
// don't want selectContentStoreRoot
const selectRoot = root => root;

const selectContentStoreRoot = store => {
    return store.content;
};

const selectContentTree = createSelector(
    selectContentStoreRoot,
    store => store.content
);

const selectWordDataTree = createSelector(
    selectContentStoreRoot,
    state => state.wordData
);

const selectAllContent = createSelector(selectContentTree, R.values);

// (root, id) => content
export const selectContent = createSelector(
    selectContentTree,
    param(1),
    (tree, id) => tree[id]
);
export const getContent = deprecated(
    selectContent,
    `Use selectContent instead`
);

// (state, section) => [ ...content ]
export const selectContentForSection = createSelector(
    selectAllContent,
    param(1),
    (allContent, section) => {
        if (!section) {
            throw new Error(`missing Section`);
        }
        const sameSection = content => {
            return content.section_id == section.id;
        };

        return R.filter(sameSection, allContent).sort(({ title }, contentB) => {
            return title.localeCompare(contentB.title);
        });
    }
);

// (state, section) => [ ...content ]
export const getSortedContentForSection = createSelector(
    selectContentForSection,
    R.sortBy(R.prop('title'))
);

const notEmptyString = s => s !== '';
const notInCollection = content =>
    content.tags.filter(notEmptyString).length === 0;

// (state, section) => [ ...content ]
export const selectNonCollectionContentForSection = createSelector(
    selectContentForSection,
    (state, section) => isInstructorInSection(state, section),
    (content, isInstructor) => {
        const finalized = content => content.is_finalized || isInstructor;
        return content.filter(finalized).filter(notInCollection);
    }
);

const selectSettingsRoot = createSelector(
    selectContentStoreRoot,
    store => store.contentSettings
);

// (root, content) => contentSettings
const selectSettingsForContent = createSelector(
    selectSettingsRoot,
    (_, content) => content.id,
    (settings, id) => R.propOr(defaultContentSettings, id, settings)
);

// (root, content) => number
export const selectZoomScale = createSelector(
    selectSettingsForContent,
    settings =>
        matchMedia('screen and (max-width: 768px)').matches ? 1 : settings.scale
);
export const getZoomScale = deprecated(
    selectZoomScale,
    `use selectZoomScale instead`
);

// (root, content) => ?
export const selectScaleCenter = createSelector(
    selectSettingsForContent,
    settings => settings.scaleCenter
);
export const getScaleCenter = deprecated(
    selectScaleCenter,
    `use selectScaleCenter instead`
);

// (root, content) => boolean
export const selectShowOthersHighlights = createSelector(
    selectSettingsForContent,
    settings => settings.showOthersHighlights
);
export const getShowOthersHighlights = deprecated(
    selectShowOthersHighlights,
    `use selectShowOthersHighlights instead`
);

// (state, content, pagenum) => Page
export const selectPage = (_, content, pagenum) => {
    if (pagenum == null) {
        throw new Error('Missing pagenum');
    }
    if (content == null) {
        throw new Error('Missing content');
    }

    if (content.pages != null) {
        const samePage = p => p.pagenum == pagenum;
        const index = R.findIndex(samePage, content.pages);
        if (index > -1) {
            return pageDecompressor(content.pages, index);
        }
    }
};
export const getPage = deprecated(selectPage, `Use selectPage instead`);

export function getPageRange(_, content, first_pagenum, last_pagenum) {
    return content.pages.filter(
        p => first_pagenum <= p.pagenum && p.pagenum <= last_pagenum
    );
}

// (root, content) => boolean
export const hasSharing = createSelector(
    selectContentStoreRoot,
    (_, content) => content.id,
    (state, id) => R.has(id, state.sharing)
);

// (root, content) => sharingSettings
export const getSharing = createSelector(
    selectContentStoreRoot,
    (_, content) => content.id,
    (state, id) => {
        return R.propOr(defaultSharingSettings, id, state.sharing);
    }
);

// (root, content) => boolean
export const hasSharingStudents = createSelector(
    selectContentStoreRoot,
    param(1),
    (state, content) => {
        const key = `/api/v3/section/${content.section_id}/`;
        return R.has(key, state.sharingStudents);
    }
);

export const getSharingStudents = createSelector(
    selectContentStoreRoot,
    param(1),
    (state, content) => {
        const key = `/api/v3/section/${content.section_id}/`;

        return R.propOr(defaultSharingStudents, key, state.sharingStudents);
    }
);

// (root, content, pagenum) => Page
export const selectNextPage = createSelector(
    selectPage,
    param(1),
    (currentPage, content) => {
        if (currentPage != null) {
            const tmp = R.compose(
                R.head,
                R.filter(greaterThan(currentPage)),
                R.prop('pages')
            );

            return tmp(content);
        }
    }
);

// (root, content, pagenum) => Page
export const selectPrevPage = createSelector(
    selectPage,
    param(1),
    (currentPage, content, pagenum) => {
        if (currentPage != null) {
            const tmp = R.compose(
                R.last,
                R.filter(lessThan(currentPage)),
                R.prop('pages')
            );

            return tmp(content);
        }
    }
);

const findMaxNumber = R.compose(R.reduce(R.max, ''), R.map(Number));

// (state, content, pagenum) => string
export const selectImageForPage = createSelector(
    (state, content, pagenum) => selectPage(state, content, pagenum),
    param(1),
    param(3),
    (page, content) => {
        const dpi = findMaxNumber(content.sizes);

        const imgsrc = `${content.image_base}${dpi}/${page.image}`;

        return `${content.cdn_domain}${imgsrc}?${content.page_signatures}`;
    }
);
export const getImageForPage = deprecated(
    selectImageForPage,
    `Use selectImageForPage instead`
);

const CONTENT_ROOT = '/api/v4/content/';
export const selectWordsURL = createSelector(
    (state, content, pagenum) => selectPage(state, content, pagenum),
    param(1),
    (page = {}, content) => {
        return `${CONTENT_ROOT}${content.slug}/text/${page.pagenum}/`;
    }
);

// (root, content) => string
export const selectCoverImage = createSelector(
    selectRoot,
    param(1),
    (root, content) => {
        if (content.media_type == 'text' && content.is_finalized) {
            return `https://dsphz1zgugixk.cloudfront.net/new_covers/${content.slug}/cover.png`;
        }

        const cover_override = content.link_cover_override;
        if (cover_override != null) {
            const [url] = cover_override.split('?');
            const [
                protocol,
                empty, // eslint-disable-line no-unused-vars
                ...rest
            ] = url.split('/');

            // TODO 8-8-22 stripping leading whitespace
            // This shouldn't have to be handled here, and a long term solution should involve
            // enforcing no white space at insertion
            switch (protocol.replace(/^\s+/g, '')) {
                case 'http:':
                case 'https:':
                    // TODO 8-8-22 bandaid to address whitespace in http/https cover links
                    return new URL(cover_override).href;
                case 'youtube:':
                    return `http://img.youtube.com/vi/${rest[0]}/0.jpg`;
                case 'vimeo:': {
                    return undefined;
                }
            }
        }
    }
);
export const getCoverImage = deprecated(
    selectCoverImage,
    `Use selectCoverImage instead`
);

export const selectChapters = createSelector(
    selectContentStoreRoot,
    param(1),
    (state, content) => {
        if (content == null) {
            throw new Error('Missing content');
        }
        const chapters = [];

        const getPageLabel = pagenum =>
            selectPage(state, content, pagenum).label;

        if (content.TOC.length <= 1) {
            const firstPagenum = R.head(content.pages).pagenum;
            const lastPagenum = R.last(content.pages).pagenum;
            chapters.push(
                chapter({
                    name: 'Pages',
                    first_page_label: getPageLabel(firstPagenum),
                    last_page_label: getPageLabel(lastPagenum),
                    first_page: firstPagenum,
                    last_page: lastPagenum,
                })
            );
        } else {
            // TOC only marks the first page of a chapter.
            content.TOC.reduce(
                (chapterStart, nextChapter) => {
                    const chapterEnd =
                        chapterStart.page === nextChapter.page
                            ? chapterStart.page
                            : nextChapter.page - 1;

                    chapters.push(
                        chapter({
                            name: chapterStart.name,
                            first_page_label: getPageLabel(chapterStart.page),
                            last_page_label: getPageLabel(chapterEnd),
                            first_page: chapterStart.page,
                            last_page: chapterEnd,
                        })
                    );
                    return nextChapter;
                } /* The initial value is the 1st chapter */
            );

            // Because we need a lower and upper boundry, the last
            // chapter needs to be generated outside the loop.

            const lastChapterStart = R.last(content.TOC);
            const lastPagenum = R.last(content.pages).pagenum;
            chapters.push(
                chapter({
                    name: lastChapterStart.name,
                    first_page_label: getPageLabel(lastChapterStart.page),
                    last_page_label: getPageLabel(lastPagenum),
                    first_page: lastChapterStart.page,
                    last_page: lastPagenum,
                })
            );
        }

        return chapters;
    }
);

export const selectUserVisibleListOfChapters = (rootState, content) => {
    if (content.TOC.length === 0) {
        return null;
    }

    return selectChapters(rootState, content);
};

export const getChapters = deprecated(
    selectChapters,
    `use selectChapters instead`
);

// (state, content, pagenum) => ?
export const getWordData = createSelector(
    selectWordDataTree,
    selectWordsURL,
    (wordData, url) => {
        if (url != null && wordData[url] != null) {
            return wordDecompressor(wordData[url]);
        }
        return {};
    }
);



// WEBPACK FOOTER //
// ./src/selectors/content.js